import axios from "axios";
import authHeader from "../auth/auth-header";
import services from "@/config/services";
import Cookies from "js-cookie";

/**
 * Servizio per la gestione dell'invio dei record alla PDC
 *
 * Questo servizio gestisce:
 * - Upload di file CSV contenenti multipli record
 * - Invio di singoli record tramite form
 * - Recupero dei record in stato KO
 * - Reinvio di record falliti
 */
export default class InvioPDCService {
  constructor(eventBus) {
    this.eventBus = eventBus;
    this.baseUrl = `${services.ask_pratiche_api.url}`;

    axios.interceptors.request.use((req) => {
      req.headers["Authorization"] = authHeader();
      req.headers["Params"] =
        Cookies.get("google_access_token") || Cookies.get("ask_access_token");
      return req;
    });
  }

  /**
   * Endpoint: POST /api/pdc/upload-csv
   *
   * Descrizione:
   * Carica un file CSV contenente uno o più record da inviare alla PDC
   * per il tipo di pratica specificato.
   *
   * @param {File} file - File CSV da caricare
   * @param {string} tipoPratica - Tipo di pratica (cessazione, voltura, etc.)
   * @returns {Promise<Object>} Risposta con esito caricamento e record processati
   */
  async uploadCSVPDC(file, tipoPratica) {
    try {
      const url = `${this.baseUrl}/management/pdc/upload-csv`;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("tipo_pratica", tipoPratica);

      const response = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      console.error("Errore durante l'upload del CSV:", error);

      // Mock response per sviluppo frontend (rimuovere quando API sarà disponibile)
      if (error.response?.status === 404) {
        console.warn("API non ancora implementata - Ritorno dati mock");
        return {
          success: true,
          message: "File processato con successo (MOCK)",
          record_processati: 10,
          record_ok: 8,
          record_ko: 2,
        };
      }

      throw error;
    }
  }

  /**
   * Endpoint: POST /api/pdc/invio-record
   *
   * Descrizione:
   * Invia un singolo record alla PDC compilato manualmente tramite form.
   *
   * @param {string} tipoPratica - Tipo di pratica
   * @param {Object} datiRecord - Dati del record da inviare
   * @returns {Promise<Object>} Risposta con esito invio
   */
  async inviaRecordPDC(tipoPratica, datiRecord) {
    try {
      const url = `${this.baseUrl}/management/pdc/upload-record`;

      const payload = {
        tipo_pratica: tipoPratica,
        ...datiRecord,
        // Aggiungi timestamp invio
        data_invio: new Date().toISOString(),
      };

      const response = await axios.post(url, payload);

      return response.data;
    } catch (error) {
      console.error("Errore durante l'invio del record:", error);

      // Mock response per sviluppo frontend
      if (error.response?.status === 404) {
        console.warn("API non ancora implementata - Ritorno dati mock");
        return {
          success: true,
          message: "Record inviato con successo (MOCK)",
          id_invio: Math.floor(Math.random() * 10000),
          stato: "IN_LAVORAZIONE",
        };
      }

      throw error;
    }
  }

  /**
   * Endpoint: GET /api/pdc/get-record-ko
   *
   * Descrizione:
   * Recupera tutti i record in stato KO per il tipo di pratica specificato.
   * Questi sono record il cui invio alla PDC è fallito o ha restituito errori.
   *
   * @param {string} tipoPratica - Tipo di pratica
   * @returns {Promise<Array>} Lista dei record in KO
   */
  async getRecordKOPDC(tipoPratica) {
    try {
      const url = `${this.baseUrl}/management/pdc/get-record-ko`;

      const response = await axios.get(url, {
        params: {
          tipo_pratica: tipoPratica,
        },
      });

      return response.data.records || [];
    } catch (error) {
      console.error("Errore durante il recupero dei record KO:", error);
      throw error;
    }
  }

  /**
   * Converte i campi da UPPERCASE a snake_case minuscolo
   * @private
   */
  _convertFieldsToLowercase(record) {
    const converted = {};
    for (const [key, value] of Object.entries(record)) {
      // Converte da UPPERCASE a lowercase
      converted[key.toLowerCase()] = value;
    }
    return converted;
  }

  /**
   * Endpoint: GET /api/pdc/get-record-richiesti
   *
   * Descrizione:
   * Recupera tutti i record pronti per essere inviati alla PDC (richieste).
   * Questi sono i dati originali del flusso 0050 che devono ancora essere processati.
   *
   * @param {string} tipoPratica - Tipo di pratica (sospensioni, riattivazioni, etc.)
   * @returns {Promise<Array>} Lista delle richieste da inviare
   */
  async getRecordOKPDC(tipoPratica) {
    try {
      const url = `${this.baseUrl}/management/pdc/get-record-richiesti`;

      const response = await axios.get(url, {
        params: {
          tipo_pratica: tipoPratica,
        },
      });

      // Converti i campi da UPPERCASE a lowercase
      const records = response.data.records || [];
      const convertedRecords = records.map((record) =>
        this._convertFieldsToLowercase(record)
      );

      return convertedRecords;
    } catch (error) {
      console.error("Errore durante il recupero delle richieste:", error);
      throw error;
    }
  }

  /**
   * Endpoint: POST /api/pdc/get-richiesta-originale
   *
   * Descrizione:
   * Recupera i dati completi della richiesta originale (flusso 0050)
   * utilizzando il CP_UTENTE univoco e il cod_causale. Questi dati sono necessari per
   * popolare il form di revisione quando un record è in KO.
   *
   * @param {string} cpUtente - Codice Pratica Utente (univoco)
   * @param {string} tipoPratica - Tipo di pratica
   * @param {string} codCausale - Codice causale della pratica
   * @returns {Promise<Object>} Dati completi della richiesta originale
   */
  async getRichiestaOriginalePDC(tipoPratica, cpUtente) {
    try {
      const url = `${this.baseUrl}/management/pdc/get-richiesta-originale`;

      const payload = {
        tipo_pratica: tipoPratica,
        cp_utente: cpUtente,
      };

      const response = await axios.post(url, payload);

      return response.data.records || {};
    } catch (error) {
      console.error(
        "Errore durante il recupero della richiesta originale:",
        error
      );
      throw error;
    }
  }

  /**
   * Endpoint: POST /api/pdc/reinvia-record
   *
   * Descrizione:
   * Reinvia alla PDC un record precedentemente fallito (in stato KO).
   *
   * @param {number} idRecord - ID del record da reinviare
   * @param {string} tipoPratica - Tipo di pratica
   * @returns {Promise<Object>} Risposta con esito reinvio
   */
  async reinviaRecordPDC(idRecord, tipoPratica) {
    try {
      const url = `${this.baseUrl}/management/pdc/reinvia-record`;

      const payload = {
        id: idRecord,
        tipo_pratica: tipoPratica,
        data_reinvio: new Date().toISOString(),
      };

      const response = await axios.post(url, payload);

      return response.data;
    } catch (error) {
      console.error("Errore durante il reinvio del record:", error);

      // Mock response per sviluppo frontend
      if (error.response?.status === 404) {
        console.warn("API non ancora implementata - Ritorno dati mock");
        return {
          success: true,
          message: "Record reinviato con successo (MOCK)",
          nuovo_stato: "IN_LAVORAZIONE",
          id_nuovo_invio: Math.floor(Math.random() * 10000),
        };
      }

      throw error;
    }
  }

  /**
   * Endpoint: POST /api/pdc/validate-ko
   *
   * Descrizione:
   * Valida un record in stato KO temporaneo e lo passa a KO definitivo.
   * Il POD viene cambiato da stato KO temporaneo a KO definitivo.
   *
   * @param {string} tipoPratica - Tipo di pratica (sospensioni, riattivazioni, etc.)
   * @param {string} codPod - Codice POD da validare
   * @returns {Promise<Object>} Risposta con esito validazione
   */
  async validateKoRecord(tipoPratica, codPod) {
    try {
      const url = `${this.baseUrl}/management/pdc/validate-ko`;

      const payload = {
        tipo_pratica: tipoPratica,
        cod_pod: codPod,
      };

      const response = await axios.post(url, payload);

      return response.data;
    } catch (error) {
      console.error("Errore durante la validazione KO:", error);
      throw error;
    }
  }
}

